import React, { useState, useEffect, useRef } from "react"
import { VectorType, isTouchEvent, isMouseEvent } from "../types/TouchOrMouseEvent"

type props = {
    object: HTMLElement | null,
    onDragEnd: () => void,
    onStart: () => void,
    active?: boolean,
    initializeKey: string
}

// initializeKeyが変わったときに初期化する
export function useMoveObjectVector ( {
    object,
    onDragEnd = () => { },
    onStart = () => { },
    active = true,
    initializeKey
}: props ) {

    const [ objectTouchStartCoordinates, setObjectTouchStartCoordinates ] = useState<VectorType | null>( null )
    const [ objectMoveVector, setObjectMoveVector ] = useState( { X: 0, Y: 0 } )
    const objectTouchStartCoordinatesRef = useRef( objectTouchStartCoordinates )

    useEffect( () => {
        setObjectMoveVector( { X: 0, Y: 0 } )
        setObjectTouchStartCoordinates( null )
    }, [ initializeKey ] )

    useEffect( () => {
        if ( !object ) return
        // if( objectTouchStartCoordinates ) {
        objectTouchStartCoordinatesRef.current = objectTouchStartCoordinates
        document.addEventListener( 'mousemove', onMouseOrTouchMove, { passive: false } )
        document.addEventListener( 'touchmove', onMouseOrTouchMove, { passive: false } )
        document.addEventListener( 'mouseup', onEnd )
        document.addEventListener( 'touchend', onEnd )
        // }
        return () => {
            objectTouchStartCoordinatesRef.current = objectTouchStartCoordinates
            document.removeEventListener( 'mousemove', onMouseOrTouchMove )
            document.removeEventListener( 'touchmove', onMouseOrTouchMove )
            document.removeEventListener( 'mouseup', onEnd )
            document.removeEventListener( 'touchend', onEnd )
        }
    }, [ objectTouchStartCoordinates ] )

    const onEnd = () => {
        setObjectTouchStartCoordinates( null )
        document.removeEventListener( 'mousemove', onMouseOrTouchMove )
        document.removeEventListener( 'touchmove', onMouseOrTouchMove )
    }


    useEffect( () => {
        objectTouchStartCoordinatesRef.current = objectTouchStartCoordinates
    }, [ objectTouchStartCoordinates ] )


    const onMouseOrTouchMove = ( e: TouchEvent | MouseEvent ) => {
        e.preventDefault()
        const currentObjectTouchStartCoordinates = objectTouchStartCoordinatesRef.current
        if ( !currentObjectTouchStartCoordinates ) return
        const vector = isTouchEvent( e ) ?
            {
                X: e.touches[ 0 ].clientX - currentObjectTouchStartCoordinates.X + objectMoveVector.X,
                Y: e.touches[ 0 ].clientY - currentObjectTouchStartCoordinates.Y + objectMoveVector.Y
            } :
            isMouseEvent( e ) ?
                {
                    X: e.clientX - currentObjectTouchStartCoordinates.X + objectMoveVector.X,
                    Y: e.clientY - currentObjectTouchStartCoordinates.Y + objectMoveVector.Y
                } :
                {
                    X: 0,
                    Y: 0
                }
        setObjectMoveVector( vector )
    }

    if ( !active ) return {}

    return {
        onMouseDown: ( e: React.MouseEvent ) => {
            setObjectTouchStartCoordinates( { X: e.clientX, Y: e.clientY } )
            onStart()
        },
        onTouchStart: ( e: React.TouchEvent ) => {
            setObjectTouchStartCoordinates( { X: e.touches[ 0 ].clientX, Y: e.touches[ 0 ].clientY } )
            onStart()
        },
        onMouseUp: () => {
            setObjectTouchStartCoordinates( null )
            onDragEnd()
        },
        onTouchEnd: () => {
            setObjectTouchStartCoordinates( null )
            onDragEnd()
        },
        objectMoveVector
    };

    // return {
    // 	onMouseDown: start,
    // 	onMouseUp: stop,
    // 	onMouseLeave: stop,
    // 	onTouchStart: start,
    // 	onTouchEnd: stop,
    // };
}



export function useDragVector ( onDragEnd: () => void ) {

    // オブジェクトをオリジナルの座標から動かすベクトルを表す変数 XとYをkey、動かすピクセル数をvalueに取る
    const [ objectMoveVector, setObjectMoveVector ] = useState( { X: 0, Y: 0 } )

    // ドラッグし始めた(MouseDownした)時のマウス座標 MouseDown時に保存し、MouseUp時にnullを入れてリセット
    const [ mouseMoveStartCoordinates, setMouseMoveStartCoordinates ] = useState<VectorType | null>( null )


    useEffect( () => {
        if ( !mouseMoveStartCoordinates ) return // ドラッグし始めてないので

        // ドラッグし始めたならMouseMoveをイベントリスナーに登録し、onMouseMoveでobjectを動かす用のベクトルを計算する
        document.addEventListener( 'mousemove', onMouseMove )

        // MouseUpしたらドラッグ開始座標をリセット かつ MouseMoveのイベントリスナーも解除
        document.addEventListener( 'mouseup', () => {
            setMouseMoveStartCoordinates( null )
            document.removeEventListener( 'mousemove', onMouseMove )
        } )

    }, [ mouseMoveStartCoordinates ] )



    const onMouseMove = ( e: MouseEvent ) => {

        // ドラッグ開始地点から現在のマウスまでのベクトルを計算し、最後にドラッグ終了した時のobjectが動いたベクトルに加算
        // イベントリスナーに登録したこの関数はクロージャなので、objectMoveVectorは、この中では常に最後にドラッグ終了した時のもの
        if ( !mouseMoveStartCoordinates ) return
        const vector = {
            X: e.clientX - mouseMoveStartCoordinates.X + objectMoveVector.X,
            Y: e.clientY - mouseMoveStartCoordinates.Y + objectMoveVector.Y
        }
        setObjectMoveVector( vector )
    }


    return {

        // MouseDownでドラッグ開始時の座標を保存
        onMouseDown: ( e: MouseEvent ) => {
            e.stopPropagation();
            e.preventDefault();
            setMouseMoveStartCoordinates( { X: e.clientX, Y: e.clientY } )
        },

        // MouseUp時にマウス位置をリセット
        onMouseUp: ( e: MouseEvent ) => {
            e.stopPropagation();
            onDragEnd()
            setMouseMoveStartCoordinates( null )
            setObjectMoveVector( { X: 0, Y: 0 } )
        },

        // 動かすべきベクトルをstyleとして返す
        transformX: objectMoveVector.X,
        transformY: objectMoveVector.Y,
    };
}

